import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Plus, X } from 'lucide-react';
import {
  useSubscription,
  useCreateSubscription,
  useUpdateSubscription,
} from '@/hooks/reporting/useSubscriptions';
import ReportTypeSelector from '@/components/reports/filters/ReportTypeSelector';
import type { ReportType, FrequencyType, CreateSubscriptionRequest } from '@/types/reporting-v2';

const FREQUENCY_OPTIONS: { value: FrequencyType; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

export default function SubscriptionForm() {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const isEdit = !!subscriptionId;

  const { data: existingSubscription, isLoading: loadingSubscription } = useSubscription(
    isEdit ? subscriptionId : undefined
  );
  const createSubscription = useCreateSubscription();
  const updateSubscription = useUpdateSubscription();

  const [formData, setFormData] = useState<CreateSubscriptionRequest>({
    subscription_name: '',
    description: '',
    report_type: 'organization_overview',
    frequency: 'weekly',
    is_active: true,
    report_params: {},
    recipients: [],
    email_subject: '',
    email_body: '',
  });

  const [recipientInput, setRecipientInput] = useState('');

  useEffect(() => {
    if (existingSubscription) {
      setFormData({
        subscription_name: existingSubscription.subscription_name,
        description: existingSubscription.description || '',
        report_type: existingSubscription.report_type,
        frequency: existingSubscription.frequency,
        is_active: existingSubscription.is_active,
        report_params: existingSubscription.report_params || {},
        recipients: existingSubscription.recipients || [],
        email_subject: existingSubscription.email_subject || '',
        email_body: existingSubscription.email_body || '',
      });
    }
  }, [existingSubscription]);

  const handleAddRecipient = () => {
    const email = recipientInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (!formData.recipients?.includes(email)) {
        setFormData((prev) => ({
          ...prev,
          recipients: [...(prev.recipients || []), email],
        }));
        setRecipientInput('');
      }
    }
  };

  const handleRemoveRecipient = (email: string) => {
    setFormData((prev) => ({
      ...prev,
      recipients: prev.recipients?.filter((r) => r !== email) || [],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.recipients || formData.recipients.length === 0) {
      alert('Please add at least one recipient');
      return;
    }

    try {
      if (isEdit) {
        await updateSubscription.mutateAsync({
          subscriptionId: subscriptionId!,
          params: formData,
        });
      } else {
        await createSubscription.mutateAsync(formData);
      }
      navigate('/admin/reporting/subscriptions');
    } catch (error) {
      console.error('Error saving subscription:', error);
    }
  };

  if (isEdit && loadingSubscription) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/reporting/subscriptions')}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Subscription' : 'Create Subscription'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isEdit ? 'Update automated email subscription' : 'Set up automated report delivery'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Name *
              </label>
              <input
                type="text"
                value={formData.subscription_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, subscription_name: e.target.value }))
                }
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter subscription name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter subscription description (optional)"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, is_active: e.target.checked }))
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Active (emails will be sent automatically)
              </label>
            </div>
          </div>
        </div>

        {/* Report Configuration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Configuration</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Type *
              </label>
              <ReportTypeSelector
                value={formData.report_type}
                onChange={(type) =>
                  setFormData((prev) => ({ ...prev, report_type: type }))
                }
                variant="dropdown"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Frequency *
              </label>
              <select
                value={formData.frequency}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    frequency: e.target.value as FrequencyType,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {FREQUENCY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Recipients */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recipients *</h2>
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="email"
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRecipient())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter email address"
              />
              <button
                type="button"
                onClick={handleAddRecipient}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>

            {formData.recipients && formData.recipients.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">
                  {formData.recipients.length} recipient(s)
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.recipients.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => handleRemoveRecipient(email)}
                        className="hover:text-blue-900"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Email Configuration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Configuration</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Subject
              </label>
              <input
                type="text"
                value={formData.email_subject}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, email_subject: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Custom email subject (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Body
              </label>
              <textarea
                value={formData.email_body}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, email_body: e.target.value }))
                }
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Custom email body (optional, defaults to system template)"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/reporting/subscriptions')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createSubscription.isPending || updateSubscription.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {createSubscription.isPending || updateSubscription.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {isEdit ? 'Update Subscription' : 'Create Subscription'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
